import React, { useRef } from 'react';

import type { TextAreaRef } from 'antd/es/input/TextArea';
import VoiceMicButton from 'components/common/VoiceMicButton/VoiceMicButton';
import { useSpeechInput } from 'hooks/useSpeechInput';

interface GridVoiceMicProps {
  textAreaRef: React.RefObject<TextAreaRef>;
  text: string;
  onText: (text: string) => void;
}

/**
 * Voice input for a Paddocks grid notes cell (OP-2481) — the same mic and
 * language picker as TextAreaField, tucked into the cell's corner so the row
 * stays one line (.paddocks-grid-voice, App.css). Speech goes in at the
 * cursor and focus returns to the field, so leaving it saves as usual.
 */
const GridVoiceMic: React.FC<GridVoiceMicProps> = ({
  textAreaRef,
  text,
  onText,
}) => {
  // Latest text for the recognition callback, which outlives a render.
  const textRef = useRef(text);
  textRef.current = text;

  const speech = useSpeechInput({
    onTranscript: (transcript) => {
      const current = textRef.current;
      const textarea = textAreaRef.current?.resizableTextArea?.textArea;
      const start = textarea?.selectionStart ?? current.length;
      const end = textarea?.selectionEnd ?? current.length;
      onText(current.slice(0, start) + transcript + current.slice(end));
      const cursor = start + transcript.length;
      requestAnimationFrame(() => {
        textarea?.focus();
        textarea?.setSelectionRange(cursor, cursor);
      });
    },
  });

  return (
    <VoiceMicButton
      className={`paddocks-grid-voice ${speech.isRecording ? 'is-recording' : ''}`}
      isSupported={speech.isSupported}
      isRecording={speech.isRecording}
      lang={speech.lang}
      langOptions={speech.langOptions}
      onToggle={speech.toggle}
      onLangChange={speech.setLang}
    />
  );
};

export default GridVoiceMic;
